
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/context/AuthContext';
import { AdminProvider } from '@/context/AdminContext';
import PwaInstallPrompt from '@/components/ui-elements/pwa-install-prompt';
import LoadingState from '@/components/dashboard/LoadingState';

// Eager load critical components
import Home from '@/pages/Home';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import AccountSettingsPage from '@/pages/AccountSettingsPage';

// Error Boundary for lazy-loaded components
const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const handleError = () => {
      setHasError(true);
      console.error("Failed to load module. Attempting to recover...");
      // Clear any stale service worker cache that might be causing issues
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.update();
          });
        });
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-8">
        <h2 className="text-xl font-bold mb-4">Module Loading Error</h2>
        <p className="mb-4 text-center">An error occurred while loading this page.</p>
        <button 
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
          onClick={() => {
            setHasError(false);
            window.location.reload();
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

// Lazy load with retry mechanism for problematic components
const lazyLoadWithRetry = (importFn) => {
  return lazy(() => {
    const retryImport = () => {
      return importFn().catch(error => {
        console.error("Error loading module, retrying:", error);
        // Add a small delay before retrying
        return new Promise(resolve => setTimeout(resolve, 1000)).then(retryImport);
      });
    };
    return retryImport();
  });
};

// Lazy load non-critical components with retry mechanism
const UserAccount = lazyLoadWithRetry(() => import('@/pages/UserAccount'));
const AdminDashboard = lazyLoadWithRetry(() => import('@/pages/AdminDashboard'));
const AdminBadges = lazyLoadWithRetry(() => import('@/pages/AdminBadges'));
const AdminEvents = lazyLoadWithRetry(() => import('@/pages/AdminEvents'));
const AdminSettings = lazyLoadWithRetry(() => import('@/pages/AdminSettings'));
const AdminApplicationsPage = lazyLoadWithRetry(() => import('@/pages/AdminApplicationsPage'));
const AdminQuickInterestPage = lazyLoadWithRetry(() => import('@/pages/AdminQuickInterestPage'));
const UserApplicationsPage = lazyLoadWithRetry(() => import('@/pages/UserApplicationsPage'));
const ProjectApplicationPage = lazyLoadWithRetry(() => import('@/pages/ProjectApplicationPage'));
const PrivacyPolicy = lazyLoadWithRetry(() => import('@/pages/PrivacyPolicy'));
const PublicEventsPage = lazyLoadWithRetry(() => import('@/pages/PublicEventsPage'));
const PrivacySettingsPage = lazyLoadWithRetry(() => import('@/pages/PrivacySettingsPage'));
const PartnerWithUsPage = lazyLoadWithRetry(() => import('@/pages/PartnerWithUsPage'));
const AdminQuests = lazyLoadWithRetry(() => import('./pages/AdminQuests'));

const App = () => {
  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(registration => {
            console.log('Service Worker registered successfully:', registration);
          })
          .catch(error => {
            console.error('Service Worker registration failed:', error);
          });
      });
    }
    
    // Preload critical routes
    const preloadRoutes = ['/dashboard', '/admin'];
    preloadRoutes.forEach(route => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = route;
      document.head.appendChild(link);
    });
  }, []);

  return (
    <AuthProvider>
      <AdminProvider>
        <Router>
          <Suspense fallback={<LoadingState />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<LoginPage />} />
              {/* Redirect /auth to /login */}
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              {/* Redirect /signup to /login as well */}
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              
              {/* All admin routes protected with AdminProtectedRoute */}
              <Route element={<AdminProtectedRoute />}>
                <Route path="/admin" element={
                  <ErrorBoundary>
                    <AdminDashboard />
                  </ErrorBoundary>
                } />
                <Route path="/admin/badges" element={
                  <ErrorBoundary>
                    <AdminBadges />
                  </ErrorBoundary>
                } />
                <Route path="/admin/events" element={
                  <ErrorBoundary>
                    <AdminEvents />
                  </ErrorBoundary>
                } />
                <Route path="/admin/settings" element={
                  <ErrorBoundary>
                    <AdminSettings />
                  </ErrorBoundary>
                } />
                <Route path="/admin/applications" element={
                  <ErrorBoundary>
                    <AdminApplicationsPage />
                  </ErrorBoundary>
                } />
                <Route path="/admin/quick-interest" element={
                  <ErrorBoundary>
                    <AdminQuickInterestPage />
                  </ErrorBoundary>
                } />
                <Route path="/admin/quests" element={
                  <ErrorBoundary>
                    <AdminQuests />
                  </ErrorBoundary>
                } />
              </Route>
              
              <Route path="/account" element={<UserAccount />} />
              <Route path="/account/settings" element={<AccountSettingsPage />} />
              <Route path="/privacy/settings" element={<PrivacySettingsPage />} />
              
              {/* Remove the ProjectsPage route and redirect it to applications */}
              <Route path="/projects" element={<Navigate to="/applications" replace />} />
              
              <Route path="/projects/apply" element={<ProjectApplicationPage />} />
              <Route path="/applications" element={<UserApplicationsPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/festi-pass" element={<PublicEventsPage />} />
              <Route path="/partner" element={<PartnerWithUsPage />} />
            </Routes>
          </Suspense>
          <PwaInstallPrompt />
          <Toaster />
        </Router>
      </AdminProvider>
    </AuthProvider>
  );
};

export default App;
